import {defineNuxtPlugin} from '#app'

export default defineNuxtPlugin(nuxtApp => {
    const VERSION_KEY = 'amosweb-version'

    // The backend version (folded into the cache key) — so a release that changes the CI defaults
    // invalidates stale localStorage themes. Captured from the Amosweb-Version response header below;
    // '0' until the first response of a fresh browser is seen.
    function appVersion() {
        try {
            return localStorage.getItem(VERSION_KEY) || '0'
        } catch {
            return '0'
        }
    }

    // CI resolves per entity, not per host and not per page type: /event/:id and /appointment/:id serve the
    // *event's mandant* CI, while /eventIFrame/:id serves that iframe's. So the id has to stay in the key —
    // dropping it made every event on a host share one cached theme, and a tenant whose mandanten have
    // different CI got a first-paint flash of the wrong brand (this is a warm-start cache for FOUC, so a
    // wrong hit is visible before the API answers). Versioned so default bumps reset.
    function storageKey() {
        const path = window.location.pathname
            .split('/')
            .filter(Boolean)
            .join(':') || 'index'

        return `ci:${appVersion()}:${window.location.hostname}:${path}`
    }

    // Public-frontend counterpart of client/111.axios's version capture: record the backend version
    // from the Amosweb-Version header so storageKey() can invalidate on a release, and drop CI themes
    // left behind by other versions. Stored in localStorage (read synchronously at boot, unlike a store).
    function captureVersion(version) {
        if (!version) return
        try {
            if (localStorage.getItem(VERSION_KEY) === version) return
            localStorage.setItem(VERSION_KEY, version)
            Object.keys(localStorage)
                .filter(key => key.startsWith('ci:') && !key.startsWith(`ci:${version}:`))
                .forEach(key => localStorage.removeItem(key))
        } catch {
            // localStorage unavailable — no-op
        }
    }

    nuxtApp.$axios?.interceptors.response.use(response => {
        captureVersion(response.headers?.['amosweb-version'])
        return response
    })

    const defaults = {
        '--custom-primary-color': '#18298C',
        '--custom-heading-color': '#32325d',
        '--custom-text-color': '#525f7f',
        '--custom-primary-contrast-color': '#ffffff',
        '--custom-secondary-color': '#8898aa',
        '--custom-link-color': '#18298C',
        '--custom-background-color': '#f8f9fe',
        '--custom-surface-color': '#ffffff',
        '--custom-font-family': 'Open Sans',
        '--custom-heading-font-family': 'Open Sans',
        '--custom-font-size': '16px',
        '--custom-heading-font-weight': '300',
        '--custom-border-radius': '6px',
        '--fc-button-bg-color': '#18298C',
        '--fc-button-hover-bg-color': '#18298C',
        '--fc-button-active-bg-color': '#18298C',
        '--fc-button-border-color': '#18298C',
        '--fc-button-hover-border-color': '#18298C',
        '--fc-button-active-border-color': '#18298C',
    }

    function applyVars(vars) {
        const el = document.documentElement
        Object.entries(vars).forEach(([k, v]) => el.style.setProperty(k, v))
        el.classList.add('inject-theming')
    }

    // Keying per entity (above) means one entry per visited event/iframe rather than one per page type, so the
    // set is bounded here instead. This is only a warm-start cache — an evicted entry costs one paint at the
    // defaults, nothing more — and the origin is shared with the other frontends, so it must not creep.
    const MAX_CACHED_THEMES = 24

    function pruneToLimit() {
        const keys = Object.keys(localStorage).filter(key => key.startsWith(`ci:${appVersion()}:`))
        // Insertion order is not retrievable, so evict arbitrarily; correctness never depends on a hit.
        keys.slice(0, Math.max(0, keys.length - (MAX_CACHED_THEMES - 1)))
            .forEach(key => localStorage.removeItem(key))
    }

    function persistToStorage(ci) {
        try {
            pruneToLimit()
            localStorage.setItem(storageKey(), JSON.stringify(ci))
        } catch {
            // localStorage full or unavailable — no-op
        }
    }

    function loadFromStorage() {
        try {
            const stored = localStorage.getItem(storageKey())
            return stored ? JSON.parse(stored) : null
        } catch {
            return null
        }
    }

    function applyAssets(assets) {
        // if favicon is provided, apply it to the document head
        if (assets?.faviconUrl) {
            const link = document.querySelector('link[rel="icon"]') || document.createElement('link')
            link.rel = 'icon'
            link.href = assets.faviconUrl
            document.head.appendChild(link)
        } else {
            // load default
            // {rel: 'icon', type: 'image/png', href: '/app/favicons/favicon_16.png', sizes: "16x16"},
            // {rel: 'icon', type: 'image/png', href: '/app/favicons/favicon_32.png', sizes: "32x32"},
            // {rel: 'icon', type: 'image/png', href: '/app/favicons/favicon_64.png', sizes: "64x64"},
            // {rel: 'icon', type: 'image/png', href: '/app/favicons/favicon_80.png', sizes: "80x80"},
            // {rel: 'icon', type: 'image/png', href: '/app/favicons/favicon_128.png', sizes: "128x128"}
            const link = document.querySelector('link[rel="icon"]') || document.createElement('link')
            link.rel = 'icon'
            link.href = '/app/favicons/favicon_32.png'
            document.head.appendChild(link)
        }
    }

    function applyPageTitle(params, pageTitle = null) {
        if (!params?.pageTitle) return
        const appName = pageTitle || nuxtApp.$config?.public?.appName
        document.title = appName ? `${appName} | ${params.pageTitle}` : params.pageTitle
    }

    function applyCi(ci, {standalone = false, pageTitle = null} = {}) {
        if (!ci?.cssVars) return
        applyAssets(ci.assets)
        applyVars(ci.cssVars)
        applyPageTitle(ci.params, pageTitle)
        persistToStorage(ci)
        if (standalone) document.documentElement.classList.add('inject-theming-standalone')
    }

    function applyCiDefaults({standalone = false} = {}) {
        applyVars(defaults)
        if (standalone) document.documentElement.classList.add('inject-theming-standalone')
    }

    // On startup: try localStorage first (instant tenant CI), fall back to hardcoded defaults
    const cached = loadFromStorage()
    if (cached) {
        applyVars(cached.cssVars ?? {})
        applyAssets(cached.assets ?? {})
        applyPageTitle(cached.params ?? {})
    } else {
        applyCiDefaults()
    }

    nuxtApp.vueApp.provide('applyCi', applyCi)
    nuxtApp.vueApp.provide('applyCiDefaults', applyCiDefaults)

    return {
        provide: {
            applyCi,
            applyCiDefaults,
        }
    }
})
